import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { withDevtools } from '@pc/store';
import { PolicyDateType } from '../../pages/trip-details/when-are-you-travelling/model/calendar-restrictions.model';
import { EProcessType } from '@pc/common';
import { BasicQuoteRequest } from '../../shared/models/quote/basic-quote.interface';
import { DestinationType } from '../../shared/models/destination-response.interface';
import { computed, inject } from '@angular/core';
import { finalize, retry } from 'rxjs';
import { PcaStatusService } from '../../shared/services/pca-status.service';
import { MakeQuoteResponse } from '../../shared/models/quote/make-quote-response.interface';
import { PolicyResult } from '../../shared/models/policy/make-policy-response-model';
import { ReferredById } from '../../core/models/process-types.enum';

interface MainState {
  isLoading: boolean;
  policyType: PolicyDateType;
  processType: EProcessType;
  policyDestinationType: DestinationType;
  isSubmitButtonInactive: boolean;
  isNewUser: boolean;
  basicQuotePayload: BasicQuoteRequest | null;
  pdsFilePath: string | null;
  continueButton: string;
  isPcaSalesProcessActive: boolean | null;
  makeQuoteResponse: MakeQuoteResponse | null;
  makePolicyResult: PolicyResult | null;
  isNavigationLocked: boolean;
  policyHttpError: boolean;
}

export const MainInitialState: MainState = {
  isLoading: false,
  isSubmitButtonInactive: true,
  isNewUser: true,
  policyType: PolicyDateType.DAILY,
  processType: EProcessType.REGULAR,
  policyDestinationType: DestinationType.Abroad,
  basicQuotePayload: null,
  pdsFilePath: null,
  continueButton: 'Continue',
  isPcaSalesProcessActive: null,
  makeQuoteResponse: null,
  makePolicyResult: null,
  isNavigationLocked: false,
  policyHttpError: false
};

export type MainStore = InstanceType<typeof MainStore>;

export const MainStore = signalStore(
  { providedIn: 'root' },
  withDevtools('MainStore'),
  withState(MainInitialState),

  withMethods((store, pcaStatusService = inject(PcaStatusService)) => ({
    setIsLoading(isLoading: boolean): void {
      patchState(store, { isLoading });
    },
    setPolicyType(type: PolicyDateType): void {
      patchState(store, {
        policyType: type
      });
    },
    setProcessType(type: EProcessType): void {
      patchState(store, {
        processType: type
      });
    },
    updateBasicQuotePayload(basicQuotePayload: BasicQuoteRequest): void {
      patchState(store, {
        basicQuotePayload
      });
    },
    updateSubmitButtonInactive(isSubmitButtonInactive: boolean): void {
      patchState(store, {
        isSubmitButtonInactive: isSubmitButtonInactive
      });
    },
    setPdsFilePath(path: string): void {
      patchState(store, {
        pdsFilePath: path
      });
    },
    setIsNavigationLocked(isLocked: boolean): void {
      patchState(store, {
        isNavigationLocked: isLocked
      });
    },
    setContinueButtonText(continueButton: string): void {
      patchState(store, {
        continueButton
      });
    },
    setMakePolicyResult(makePolicyResult: PolicyResult): void {
      patchState(store, {
        makePolicyResult
      });
    },
    setMakeQuoteResponse(makeQuoteResponse: MakeQuoteResponse): void {
      patchState(store, {
        makeQuoteResponse
      });
    },
    setPolicyHttpError(policyHttpError: boolean): void {
      patchState(store, {
        policyHttpError
      });
    },
    fetchPcaSalesProcessStatus(apiUrl: string): void {
      patchState(store, { isLoading: true });
      pcaStatusService
        .getActiveStatus(apiUrl)
        .pipe(
          retry(2),
          finalize(() => patchState(store, { isLoading: false }))
        )
        .subscribe({
          next: (res) => {
            patchState(store, { isPcaSalesProcessActive: res.isActive });
          },
          error: (error) => {
            patchState(store, { isPcaSalesProcessActive: false });
            throw error;
          }
        });
    }
  })),
  withComputed(({ isPcaSalesProcessActive, processType, makePolicyResult, policyHttpError }) => ({
    pcaSalesProcessIsInactive: computed(() => {
      return isPcaSalesProcessActive() !== null && !isPcaSalesProcessActive();
    }),

    getReferredById: computed(() => {
      return processType() === EProcessType.AFFILIATE
        ? ReferredById.AFFILIATE
        : processType() === EProcessType.AGENTS
          ? ReferredById.AGENTS
          : ReferredById.REGULAR;
    }),

    activePolicyError: computed(
      () => !!makePolicyResult()?.isActivePolicyError || !!makePolicyResult()?.HasPreviousPolicies
    ),
    creditCardError: computed(
      () =>
        !!makePolicyResult()?.IsCardExpired ||
        makePolicyResult()?.makePolicyResultError?.ResultErrorType === 1 ||
        policyHttpError
    )
  }))
);
